import i18next from 'i18next'
import {TKey} from '@/translations/i18n'
import dayjs from '@/dayjs'
import {capitalize} from '@/utilities/helpers'
import {
    ActivityReportSector,
    ActivityReportStatus,
    ActivityReportsColumn
} from '@/features/accesses/activity-report/types'

export const activityReportSectorToLabel = {
    outpatient: 'activity_report:sector_outpatient',
    domiciliary: 'activity_report:sector_domiciliary'
} as const satisfies Record<ActivityReportSector, TKey>

export const activityReportStatusToLabel = {
    pending: 'activity_report:status_pending',
    billed: 'activity_report:status_billed'
} as const satisfies Record<ActivityReportStatus, TKey>

export const activityReportStatusToBadgeVariant = {
    pending: 'orange',
    billed: 'success'
} as const satisfies Record<ActivityReportStatus, 'orange' | 'success'>

// The `select` column has no header label, so it is excluded from this map.
export const activityReportColumnToLabel = {
    period: 'activity_report:column_period',
    sector: 'activity_report:column_sector',
    accesses: 'activity_report:column_accesses',
    absences: 'activity_report:column_absences',
    status: 'activity_report:column_status',
    file: 'activity_report:column_file',
    createdAtAndAuthor: 'activity_report:column_created_at',
    moreActions: 'commons:actions'
} as const satisfies Record<Exclude<ActivityReportsColumn, 'select'>, TKey>

// Sector select options (key → label) for the extraction dialog.
export const activityReportSectorOptions = activityReportSectorToLabel

// Backend business error code → user-facing message (FR-029). Raw codes are never shown.
const ACTIVITY_REPORT_ERROR_LABELS: Record<string, TKey> = {
    ACCESSES_NOT_VALIDATED: 'activity_report:error_accesses_not_validated',
    NO_BILLABLE_ACCESSES: 'activity_report:error_no_billable_accesses',
    ALREADY_BILLED: 'activity_report:error_already_billed',
    INVALID_DATE_RANGE: 'activity_report:error_invalid_date_range',
    'activity-report-not-found': 'activity_report:error_not_found',
    // DSP Flux (PRI txt) export failure (feature 010, FR-014).
    'pri-export-failed': 'dsp_flux_export:error_pri_export_failed'
}

export const resolveActivityReportErrorLabel = (code: string | undefined): TKey =>
    (code ? ACTIVITY_REPORT_ERROR_LABELS[code] : undefined) ?? 'errors:default'

// File (export job) status → label for the history "File" column.
export const activityReportFileStatusToLabel = (status: string | undefined | null): TKey => {
    switch (status) {
        case 'pending':
            return 'activity_report:file_status_pending'
        case 'processing':
            return 'activity_report:file_status_processing'
        case 'ready':
            return 'activity_report:file_status_ready'
        case 'failed':
            return 'activity_report:file_status_failed'
        default:
            return 'activity_report:file_status_none'
    }
}

export const activityReportFileStatusToBadgeVariant = (
    status: string | undefined | null
): 'danger' | 'neutral' | 'orange' | 'success' => {
    switch (status) {
        case 'ready':
            return 'success'
        case 'failed':
            return 'danger'
        case 'processing':
            return 'orange'
        default:
            return 'neutral'
    }
}

// Download is offered only for a ready file; regenerate when the job is missing or in a
// terminal non-ready state (FR-022 / FR-023).
export const canDownloadActivityReportFile = (status: string | undefined | null): boolean => status === 'ready'

export const canRegenerateActivityReportFile = (status: string | undefined | null): boolean =>
    !status || status === 'failed' || status === 'expired' || status === 'archived' || status === 'cancelled'

/* --- DSP Flux (PRI txt) export (feature 010) --- */

// A 'MM-YYYY' period as a localized, capitalized 'MMM YYYY' label for the month select (e.g. "Feb 2026").
export const formatPriTxtPeriodLabel = (period: string): string =>
    capitalize(dayjs(period, 'MM-YYYY').locale(i18next.language).format('MMM YYYY'))

// Split a 'MM-YYYY' period into the {month, year} numbers the export endpoint expects.
export const periodToMonthYear = (period: string): {month: number; year: number} => {
    const [month, year] = period.split('-')
    return {month: Number(month), year: Number(year)}
}

// Extract the attachment filename from a Content-Disposition header; null when absent/unparsable
// so callers can fall back to a client-derived name.
export const parsePriTxtExportFilename = (contentDisposition?: string | null): string | null => {
    if (!contentDisposition) return null
    // RFC 5987 extended form: filename*=UTF-8''encoded-name
    const extended = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(contentDisposition)
    if (extended?.[1]) {
        try {
            return decodeURIComponent(extended[1].trim().replace(/^["']|["']$/g, '')) || null
        } catch {
            // fall through to the plain filename
        }
    }
    const plain = /filename="?([^";]+)"?/i.exec(contentDisposition)
    return plain?.[1]?.trim() || null
}
